import { ContentPaginatedInterface } from "./content.type";
import { OrderResponse } from "./order.type";

export enum DeliveryTime {
  MORNING = "MORNING",
  AFTERNOON = "AFTERNOON",
  NIGHT = "NIGHT",
}

export enum DeliveryState {
  DRAFT = "DRAFT",
  SCHEDULED = "SCHEDULED",
  REJECTED = "REJECTED",
  ACCEPTED = "ACCEPTED",
  IN_PROGRESS = "IN_PROGRESS",
  CANCELLED = "CANCELLED",
  DELIVERED = "DELIVERED",
  COMPLETED = "COMPLETED",
}

/** Lean company ref carried by a `DeliveryResponse` — `{ id, name }` only, never the secret graph. */
export interface DeliveryCompanyRef {
  id: string;
  name: string;
}

/** Lean buyer/supplier ref carried by a `DeliveryResponse` — no `password`, no secret company graph. */
export interface DeliveryUserRef {
  id: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email?: string;
  company?: DeliveryCompanyRef;
}

/** Lean site ref carried by a `DeliveryResponse` — the site's own company graph is not surfaced. */
export interface DeliverySiteRef {
  id: string;
  name?: string;
  internalCode?: string;
  address?: string;
  country?: { id: string; name?: string; nameEn?: string };
}

/**
 * Faithful projection of the backend `DeliveryResponse` (Deliveries flow, Task 2) — the shape now
 * returned by every authenticated delivery endpoint that used to hand back the raw `Delivery` entity
 * (`GET /deliveries/state/:state/:page`, `GET /deliveries/:id`, `GET /deliveries/user/:page`, the
 * create/update writes and the schedule/reschedule/confirm/mark-incomplete state mutations).
 *
 * The nested `user`/`supplier`/`company`/`site` graphs are reduced to lean refs (no `password`, no
 * secret `Company` graph — taxId/bankingInfos/annualRevenue/aiSummary). `order` reuses the already-lean
 * `OrderResponse`. The anonymous token endpoints keep their own `DeliveryConfirmationResponse`.
 */
export interface DeliveryInterface {
  id: string;
  user: DeliveryUserRef;
  orderName: string;
  deliveryDate: string;
  deliveryTime: DeliveryTime;
  purchaseOrderNumber: string;
  purchaseOrderFile: string;
  receiverComment?: string;
  order: OrderResponse;
  comment?: string;
  receiverEmail: string;
  supplierEmails: string[];
  createdAt: string;
  supplier?: DeliveryUserRef;
  site: DeliverySiteRef;
  state: DeliveryState;
  delivered: boolean;
  deleted: boolean;
  active: boolean;
  tokenUsed: boolean;
  token: string;
  company: DeliveryCompanyRef;
  deliveredAt?: string;
  receptionDate?: string;
  remittanceNumber?: string;
  remittanceFile?: string;
}

export interface DeliveryRequest {
  supplierEmails: string[];
  receiverEmail?: string;
  orderName: string;
  comment?: string;
  siteId: string | null;
  supplierId?: string;
  deliveryDate: string;
  deliveryTime: DeliveryTime;
  purchaseOrderFile: string;
  purchaseOrderNumber: string;
  orderId: string;
  user: { id: string };
  companyId: string;
  receptionDate?: string;
  remittanceNumber?: string;
  remittanceFile?: string;
}

/**
 * The anonymous delivery-token response, narrowed to the lean backend DTO
 * (`DeliveryConfirmationDetailsDTO`, Unauthenticated-flow Task 4) — no longer the raw
 * `Delivery`/`Offer` entity graph, which leaked the buyer/company/pricing graph to an
 * anonymous token holder. Carries only what `DeliveryConfirmation.tsx` renders. `tokenUsed`
 * is kept (Task 4 fix) — it drives the "already confirmed" banner.
 */
export interface DeliveryConfirmationResponse {
  delivery: {
    id: string;
    deliveryDate: string;
    deliveryTime: string;
    receiverComment: string | null;
    tokenUsed: boolean;
  };
  selectedOffers: {
    id: string;
    order: {
      material: { globalMaterialName: string };
      quantity: number;
      measurementUnit: string;
    };
  }[];
}

export interface DeliveryCountResponse {
  accepted: number;
  cancelled: number;
  delivered: number;
  draft: number;
  inProgress: number;
  incomplete: number;
  pendingConfirmation: number;
  rejected: number;
  rescheduled: number;
  scheduled: number;
  succeed: number;
}

export interface DeliveryUpdateRequest {
  deliveryId: string;
  endpoint: "schedule" | "reschedule" | "confirm" | "mark-incomplete";
}

export type PaginatedDeliveryInterface =
  ContentPaginatedInterface<DeliveryInterface>;
